import { useMemo } from 'react';
import type { RequestToken } from '../types';

/**
 * credentialUuid → (attributeUuid → formatted value string)
 */
export type IssuedValueMap = Map<string, Map<string, string>>;

/**
 * Extracts issued attribute values from an issuance request token.
 *
 * Issuance tokens embed the values directly on each query field
 * (`body.queries[].fields[].value`). This hook builds a lookup map
 * so the IssuanceReviewPage can display Attribute | Value columns.
 *
 * Returns an empty map for disclosure tokens or when no token is selected.
 */
export function useIssuedData(
  selectedToken: RequestToken | null,
): IssuedValueMap {
  return useMemo(() => {
    const valueMap: IssuedValueMap = new Map();

    if (!selectedToken) return valueMap;

    const body = selectedToken.payload.body;

    // Only issuance tokens have top-level queries with values
    if ('groups' in body) return valueMap;

    for (const query of body.queries) {
      const fieldMap = new Map<string, string>();
      for (const field of query.fields) {
        fieldMap.set(field.attributeUuid, formatValue(field.value));
      }
      valueMap.set(query.credentialUuid, fieldMap);
    }

    return valueMap;
  }, [selectedToken]);
}

function formatValue(value: unknown): string {
  if (value == null || value === '') return '-';
  if (typeof value === 'string') {
    return value.length > 150 ? '...' : value;
  }
  return String(value);
}
